"use client";

import { useEffect, useState } from "react";

type CatalogDownloadProps = {
  href: string;
  filename: string;
  label: string;
};

/**
 * Same-origin file link. The browser streams the PDF straight to disk
 * (no in-memory blob). A low-priority prefetch warms the HTTP cache so the
 * click usually starts from cache. The spinner only marks that the handoff
 * has started — it does not delay the download.
 */
export function CatalogDownload({ href, filename, label }: CatalogDownloadProps) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
    if (conn?.saveData || document.documentElement.hasAttribute("data-save-data")) return;

    const link = document.createElement("link");
    link.rel = "prefetch";
    link.as = "fetch";
    link.href = href;
    let idleId = 0;
    let timerId = 0;
    const attach = () => document.head.appendChild(link);
    if (typeof window.requestIdleCallback === "function") {
      idleId = window.requestIdleCallback(attach, { timeout: 2000 });
    } else {
      timerId = window.setTimeout(attach, 300);
    }

    return () => {
      if (idleId) window.cancelIdleCallback(idleId);
      if (timerId) window.clearTimeout(timerId);
      link.remove();
    };
  }, [href]);

  return (
    <a
      className={busy ? "pr-catalog-dl is-busy" : "pr-catalog-dl"}
      href={href}
      download={filename}
      aria-busy={busy || undefined}
      onClick={() => {
        setBusy(true);
        window.setTimeout(() => setBusy(false), 900);
      }}
    >
      <span className="pr-catalog-dl-mark" aria-hidden>
        {busy ? (
          <svg className="pr-catalog-dl-spin" viewBox="0 0 24 24">
            <circle
              cx="12"
              cy="12"
              r="8"
              fill="none"
              stroke="currentColor"
              strokeOpacity="0.16"
              strokeWidth="1.6"
            />
            <path
              d="M12 4a8 8 0 0 1 8 8"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        ) : (
          <svg className="pr-catalog-dl-icon" viewBox="0 0 24 24">
            <path
              d="M12 4.5v9.2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <path
              d="M8.4 11.2 12 14.8l3.6-3.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M5.5 19h13"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </svg>
        )}
      </span>
      <span>{label}</span>
    </a>
  );
}
